
import {APP_CONFIG} from "./GeneralConfig";
import React from "react";
import { Redirect } from "react-router-dom";
import { UserViewSettings } from '../models/common/ViewSettings'
import { IRouteConfig } from "../models/common/Routes";
import {FilterTypes} from "../helpers/FilterTypes";
import {Identifier} from "../models/common/Identifier";
import {TimesheetTaskModule} from "../models/timesheet/TimesheetTask";
import {TimesheetModule} from "../models/timesheet/Timesheet";
import { addHours } from "date-fns/esm";
import {ProjectRoleModule} from "../models/projects/ProjectRole";
import {CustomerModule} from "../models/customers/CustomerModule";
import {ProjectCommissionStateModule} from "../models/projects/ProjectCommissionState";
import {ExternalWorkGroupModule} from "../models/contractor/ExternalWorkGroup";
import {ExternalWorkModule} from "../models/contractor/ExternalWork";
import { ContractorModule } from "../models/contractor/Contractor";
import {ProjectStagePhaseModule} from "../models/projects/ProjectStagePhase";
import {ProjectId, ProjectModule, ProjectStagePhaseIdPath} from "../models/projects/Project";
import {OutgoingInvoiceModule} from "../models/accounting/AccountBook";
import {IncomingInvoiceModule} from "../models/accounting/IncomingInvoice";
import {ProjectAppendixModule} from "../models/projects/ProjectAppendixModule";
import {ProjectReportModule} from "../models/reporting/ProjectReportModule";
import {ProjectStagePhaseEmployeeModule} from "../models/projects/ProjectStagePhaseTaskEmployee";
import {EmployerModule} from "../models/employer/EmployerModule";
import {ProjectSolvencyReportModule} from "../models/reporting/ProjectSolvencyReportModule";
import {UserApiKeyModule} from "../models/api-keys/UserApiKeyModule";

/**
 * API request methods
 */
export const RouteMethods = {
    // fetch object from backend
    get: "GET",
    // create new entry
    post: "POST",
    // delete entry
    delete: "DELETE",
    // update entry
    put: "PUT",
    // partial update of entry, this is used to restore deleted entries
    patch: "PATCH"
};


/**
 * API basic header object
 */
export function basicRequestHeaders() {
    return {
        ...contentTypeHeader(),
        ...bearerTokenHeader(),
       // "Cache-Control": "no-cache, no-store, max-age=0"
    };
}

/**
 * standard headers to multipart file transfer
 */
export function multipartHeaders() {

    return {
        //multipart content type can not be set, the browser will do this automatically, when the data is formData
    };
}

/**
 * returns an object with Content-Type as the key. usable with rxjs ajax headers
 *
 * @see ContentType
 * @see AdvancedAppConfig
 *
 * @param contentType content type to be returned, defaults to json
 *
 */
export function contentTypeHeader( contentType: Object = ContentType.json) {
    return contentType;
}

/**
 * Generates an authentication token header for API requests.
 *
 * This function extracts the authentication token stored in browser cookies
 * and formats it into an HTTP header for secure communication with backend services.
 *
 * The token is retrieved from a cookie named `token` and inserted into the
 * `X-Auth-Token` header for use in authenticated API requests. This method is
 * integral for maintaining secure session handling without exposing sensitive
 * credentials directly in other parts of the application.
 *
 * @return An object containing the `X-Auth-Token` header with the extracted token as its value.
 *
 * @throws {Error} If the cookie is not found or the format is invalid, this function
 * does not handle fallback logic and will return a header with an empty token string.
 */
export function authTokenHeader() {
    return { "X-Auth-Token":document.cookie.replace(/(?:(?:^|.*;\s*)token\s*\=\s*([^;]*).*$)|^.*$/, "$1") };
}

/**
 * returns a new object with request header name: X-XSRF-TOKEN as the key
 * retrieves the actual xsrf token from cooke XSRF-TOKEN
 * @see setXsrfCookie
 * @see AdvancedAppConfig
 */
export function xsrfTokenHeader() {
    return { "X-XSRF-TOKEN":document.cookie.replace(/(?:(?:^|.*;\s*)XSRF-TOKEN\s*\=\s*([^;]*).*$)|^.*$/, "$1") };
}

/**
 * set new xsrf cookie
 */
export const setXsrfCookie = () => {
    /* sameSite=None; secure; */
        const token = generateXsrfToken();
        document.cookie = "XSRF-TOKEN="+token+"; path=/; domain=."+APP_CONFIG.baseWebDomain+"; SameSite=strict; expires="+ addHours( new Date(), 24 );

    };

/**
 * Decodes the payload from a JWT (JSON Web Token).
 *
 * This function is used to extract and parse the payload data contained within a JWT.
 * It assumes the token adheres to the standard JWT structure and uses Base64URL encoding.
 *
 * Note: This function does not verify the JWT's signature or validate its authenticity. It should be used only when the JWT is already trusted.
 *
 * @param token The JWT as a string, expected to have the standard format: header.payload.signature.
 * @returns An object representing the decoded payload extracted from the JWT.
 * @throws {SyntaxError} Throws an error if the base64-decoded payload is not valid JSON.
 * @throws {TypeError} Throws an error if the input `token` is not a string or is improperly formatted.
 */
export function decodeJwtPayload(token: string): {} {
    const base64Url = token.split('.')[1]; // Get the payload part
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload;
}

/**
 * Updates the global authentication token for the application.
 *
 * This method sets the provided token to a global variable, making it
 * accessible throughout the application for authentication purposes.
 * It does not validate or encode the token, and only assigns it directly.
 *
 * Ensure that the token provided is valid and securely obtained, as this
 * function does not perform any security checks or validations. Overwriting
 * an existing token could affect ongoing authenticated requests.
 *
 * @param token - The authentication token to be set globally. This should be
 *                a valid token string required for authenticating API requests
 *                and secure access to resources.
 * @return void - Does not return any value.
 */
export function setAuthToken( token: string) {
    window.token = token;
}

/**
 * Retrieves the authentication token stored in the global context.
 *
 * This function is designed to fetch and return the authentication token if available.
 * It ensures that invalid, empty, or undefined tokens are handled gracefully,
 * returning `null` in these cases.
 *
 * Technical Details:
 * - The function checks a global variable `window.token`.
 * - If the token is `undefined`, `null`, or an empty string, `null` is returned.
 * - Otherwise, it returns the token as a string.
 *
 * @return The valid authentication token as a string if available, or `null` if a token is missing or invalid.
 */
export function getAuthToken(): string | null {
    const token = window.token;

    if( typeof token === 'undefined' || token === null || token.length === 0 ) {
        return null;
    }

    return token;
}




/**
 * Extracts the authentication token from the `token` cookie and formats it
 * as an OAuth2-compatible `Authorization: Bearer` header.
 *
 * @returns An object containing the `Authorization` header with the Bearer token.
 */
export function bearerTokenHeader(): { Authorization?: string } {
    const token = getAuthToken();

    if( token === null ) {
        return {};
    }

   // const claim = decodeJwtPayload(token);

    //@ts-ignore
   /* if( claim.exp < Date.now() ) {
        return {};
    }*/

    return { Authorization: `Bearer ${token}` };
}

/**
 * Determines if an authentication token is close to expiring.
 * This helps ensure secure user sessions by proactively managing token expiration.
 *
 * The function evaluates the token's expiration time against the current time,
 * with a 30-second buffer to account for potential delays in request processing.
 *
 * @param token The JSON Web Token (JWT) to evaluate. It must be a valid token with a decodable payload containing an `exp` claim.
 * @return Returns `true` if the token is within 30 seconds of expiring or has already expired. Returns `false` otherwise.
 * @throws Throws an error if the token payload cannot be decoded or does not contain a valid `exp` claim.
 */
export function isAuthTokenAboutToExpire(token: string ): boolean {
    const payload = decodeJwtPayload(token) as { exp: number };
    // JWT `exp` is in seconds since epoch; Date.now() returns milliseconds
    return payload.exp * 1000 < ( Date.now() + 60 * 1000 );
}


/**
 * generate a new xsrf token to send with request
 */
const generateXsrfToken = () => {
    const validChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let array = new Uint8Array(40);
    window.crypto.getRandomValues(array);
    array = array.map(x => validChars.charCodeAt(x % validChars.length));
    return String.fromCharCode.apply(null, Array.from( array ));
} ;

/**
 * HTML content types for APP_CONFIG::getRequestObject
 * when using that function, the default is json
 * @see APP_CONFIG
 * @see AdvancedAppConfig
 */
export const ContentType = {
    json: { "Content-Type":"application/json; charset=utf-8" },
    urlEncoded: { "Content-Type":"application/x-www-form-urlencoded; charset=utf-8" },

} as const;

/**
 * encode js objects to json and add for multipart file transport
 *
 * @param keyForJson
 * @param json
 * @param files
 */
export function toFormDataEncode(keyForJson: string, json: any, files: File[]): FormData {
    const formData = new FormData();

    formData.append( keyForJson, JSON.stringify( json ));
    files.forEach( file => {
        formData.append('files[]', file )
    } )

    return formData;
}




/**
 * returns the stringified version of url params for api version 2
 * @param userViewSettings UserViewSettings object
 */
export function getUrlParamsFromViewSettings( userViewSettings: UserViewSettings ) {
   // console.log( userViewSettings );
    if( userViewSettings === null ) {
        return '?search=&filter=&sort&page=1&entriesPerPage=20'
    }

    const findAllCommasRegex = /\s*,\s*|\s+,/g;

    // get search param from viewSettings
    const searchParam = userViewSettings.searchValue !== null  ? encodeURI( userViewSettings.searchValue ) : '';
    // selected page number
    const pageNumber = `&page=${userViewSettings.selectedPageNumber}`;
    //entries per page
    const entries = `&entriesPerPage=${userViewSettings.numberOfEntriesPerPage}`;
    const separator = "_-_";
    //get sorting from user view settings
    let sort = '';
    if( userViewSettings.orderByFields && userViewSettings.orderByFields.length > 0 ) {
        sort = userViewSettings.orderByFields.map( order => {
            return `&sort=${order.fieldName}${separator}${(( order.direction ) ? 1 : 0)}`;
        } ).toString()
            .replace(findAllCommasRegex, '');
    }  else {
        sort = "&sort="
    }

    //
    let filter = "";

    if( userViewSettings.activeFilters && userViewSettings.activeFilters.length > 0 ) {
        
        filter = userViewSettings.activeFilters.map( filter => {
            const isInverted = filter.isInverted ? 1 : 0;
            let value = (
                filter.type === FilterTypes.START_DATE
                || filter.type === FilterTypes.END_DATE
                || filter.type === FilterTypes.DATE
            ) ? typeof filter.selectedValue === "number"
                    ? ( new Date( filter.selectedValue ) ).getTime()
                    : filter.selectedValue.getTime()
              : filter.selectedValue

            if( filter.type === FilterTypes.BOOL ) {
                value = filter.selectedValue ? 1 : 0;
            }

            return  "&filter=" + filter.identifier + separator + filter.type + separator + filter.displayName + separator + value + separator + isInverted;

        }).toString().replace(findAllCommasRegex,'');
    
    }

    let columnState = "&columnState=";
    if( userViewSettings.columnState && userViewSettings.columnState.length > 0 ) {
        columnState = userViewSettings.columnState.map( 
            col => ( typeof col !== 'undefined' ) ? `&columnState=${ col.columnName }${ separator }${ (col.isVisible) ? 1 : 0 }` : ''
        ).toString().replace(findAllCommasRegex, '');
    }

    return `?search=${searchParam}${filter}${sort}${pageNumber}${entries}${columnState}`;
}


/**
 * these routes are version two routes as one page application routed by react router
 * the viewIdentifier is the unique link title of the view.
 * @type {{index: {viewIdentifier: string, main: {path: string, exact: boolean, component: function(): *}}, dashboard: {viewIdentifier: string, main: {path: string, component: *}, formNew: {path: string, component: *}}, projectLogEntry: {viewIdentifier: string, main: {path: string, link: function(*, *, *): string, component: *}}, userGroups: {viewIdentifier: string, main: {path: string}}, user: {viewIdentifier: string, logout: {path: string, component: function(): string}}}}
 */
export const RouteConfig: IRouteConfig = {
    index: {
        viewIdentifier: 'index',
        routes: {
                main: {
                    path: '/',
                    authenticationOptional: true,
                    exact: true,
                    component: React.lazy( () => import('../components/common/LandingPage'))
                }
        }
    },
    dashboard: {
        viewIdentifier: 'dashboard',
        routes: {
            main: {
                path: '/dashboard',
                exact: true,
                component: React.lazy( () => import('../components/projects/ProjectLog') )
            },
            newEntry: {
                path: '/dashboard/new',
                exact: true,
                component: React.lazy( () => import('../components/projects/ProjectLogEntryFormView') )
            }
        }
    },
    userCP: {
        viewIdentifier: "userCP",
        routes: {
            main: {
                path: '/usercp',
                exact: true,
                component: React.lazy( () => import('../components/user/UserCPView') )
            }
        }
    },
    timesheet: {
        viewIdentifier: TimesheetModule.identifier,
        routes: {
            main: {
                path: '/timesheet',
                exact: true,
                component: React.lazy( () => import( '../components/timesheet/TimesheetList' ) )
            },
            detail: {
                path: '/timesheet/:timesheetId/detail/:origin?',
                link: ( id: number, origin: string | undefined = undefined ) => `/timesheet/${id}/detail/${origin}`,
                exact: true,
                component: React.lazy( () => import('../components/timesheet/TimesheetDetail') )
            },
            editEntry: {
                path: '/timesheet/:timesheetId/edit/:origin?',
                exact: true,
                link: ( timesheetId: number, origin: string | undefined = undefined ) => `/timesheet/${ timesheetId }/edit/${origin}`,
                component: React.lazy(  () => import('../components/timesheet/TimesheetForm') )
            },
            newEntry: {
                path: '/timesheet/new/:startTime?/:origin?',
                link: ( startTime: number, origin: string | undefined = undefined ) => `/timesheet/new/${ startTime }/${origin}`,
                component: React.lazy( () => import('../components/timesheet/TimesheetForm') )
            },
        }
    },
    timesheetTasks: {
        viewIdentifier: TimesheetTaskModule.identifier,
        routes: {
            main: {
                path: '/timesheet_tasks',
                link: () => '/timesheet_tasks',
                exact: true,
                component: React.lazy( () => import('../components/timesheet_tasks/TimesheetTasksView') )
            },
            newEntry: {
                path: '/timesheet_tasks/new',
                exact: true,
                component: React.lazy(  () => import('../components/timesheet_tasks/TimesheetTaskFormView') )
            },
            detail: {
                path: '/timesheet_tasks/:taskId/detail',
                link: ( id: number ) => `/timesheet_tasks/${id}/detail`,
                exact: true,
                component: React.lazy( () => import( '../components/timesheet_tasks/TimesheetTasksDetail' ) )
            },
            editEntry: {
                path: '/timesheet_tasks/:taskId/edit',
                link: ( id: number ) => `/timesheet_tasks/${id}/edit`,
                exact: true,
                component: React.lazy( () => import('../components/timesheet_tasks/TimesheetTaskFormView') )
            },
            predefNew: {
                path: '/timesheet_tasks/:taskId/description/new',
                exact: true,
                link: ( taskId: Identifier<number> ) => `/timesheet_tasks/${ taskId.rawValue }/description/new`,
                component: React.lazy( () => import('../components/timesheet_tasks/TimesheetTaskDescriptionFormView') )
            },
            predefEdit: {
                path: '/timesheet_tasks/:taskId/description/:predefDescriptionId/edit',
                exact: true,
                link: ( taskId: Identifier<number>, predefDescriptionId: Identifier<number> ) =>
                    `/timesheet_tasks/${ taskId.rawValue }/description/${ predefDescriptionId.rawValue }/edit`,
                component: React.lazy( () => import('../components/timesheet_tasks/TimesheetTaskDescriptionFormView') )
            }
        }
    },
    projectLog: {
        viewIdentifier: 'project_log',
        routes: {
            thread: {
                path: '/projects/:projectId/log/:logId/thread/:logEntryId',
                exact: true,
                link: (
                    projectId: Identifier<number>,
                    entryLogId: number,
                    logId: number
                ) => "/projects/"+projectId.rawValue+"/log/"+logId+"/thread/"+entryLogId,
                component: React.lazy( () => import('../components/projects/ProjectLogEntryThread') )
            },
            newAnswer: {
                path: '/projects/log/:logId/thread/:answerToEntryId/answer/',
                exact: true,
                link: (
                    entryLogId: number,
                    logId: number
                ) => `/projects/log/${ logId }/thread/${ entryLogId }/answer/`,
                component: React.lazy( () => import('../components/projects/ProjectLogEntryFormView') )
            }
        }
    },
    project: {
        viewIdentifier: ProjectModule.identifier,
        routes: {
            main: {
                path: "/projects",
                exact: true,
                component: React.lazy( () => import('../components/projects/ProjectListView') )
            },
            editEntry: {
                path: "/projects/:projectId/edit",
                exact: true,
                link: ( projectId: Identifier<number> ) => `/projects/${ projectId.rawValue }/edit`,
                component: React.lazy( () => import('../components/projects/form/ProjectFormView') )
            },
            newEntry: {
                path: '/projects/new',
                exact: true,
                component: React.lazy(() => import('../components/projects/form/ProjectFormView'))
            },
            detail: {
                path: "/projects/:projectId/detail",
                exact: true,
                link: ( projectId: Identifier<number> ) => `/projects/${ projectId.rawValue }/detail`,
                component: React.lazy( () => import('../components/projects/detail/ProjectDetailView') )
            },
        }
    },
    projectPhase: {
        viewIdentifier: ProjectStagePhaseModule.identifier,
        routes: {
            newEntry: {
                path: "/projects/:projectId/stages/:stageId/phases/new",
                exact: true,
                link: ( phaseIdPath: ProjectStagePhaseIdPath ) => `/projects/${ phaseIdPath.projectId.rawValue }/stages/${ phaseIdPath.projectStageId.rawValue }/phases/new`,
                component: React.lazy( () => import('../components/projects/form/ProjectPhaseFormView') )
            },
            editEntry: {
                path: "/projects/:projectId/stages/:stageId/phases/:phaseId/edit",
                exact: true,
                link: ( phaseIdPath: ProjectStagePhaseIdPath ) => `/projects/${ phaseIdPath.projectId.rawValue }/stages/${ phaseIdPath.projectStageId.rawValue }/phases/${ phaseIdPath.phaseId.rawValue }/edit`,
                component: React.lazy( () => import('../components/projects/form/ProjectPhaseFormView') )
            }
        }
    },
    /*projectEmployee: {
        viewIdentifier: ProjectStagePhaseEmployeeModule.identifier,
        routes: {
            newEntry: {
                path: "/projects/:projectId/employee/new",
                exact: true,
                link: (projectId: Identifier<number>) => `/projects/${projectId.rawValue}/employee/new`,
                component: React.lazy(() => import('../components/projects/form/ProjectEmployeeFormView'))
            }
        }
    },*/
    projectAppendix: {
        viewIdentifier: ProjectAppendixModule.identifier,
        routes: {
            newEntry: {
                path: "/projects/:projectId/appendix/new",
                exact: true,
                link: ( projectId: Identifier< number > ) => `/projects/${ projectId.rawValue }/appendix/new`,
                component: React.lazy( () => import('../components/projects/form/ProjectAppendixFormView') )
            },
            editEntry: {
                path: "/projects/:projectId/appendix/:projectAppendixId/edit",
                exact: true,
                link: (
                    projectId: Identifier< number >,
                    projectAppendixId: Identifier< number >
                ) => `/projects/${ projectId.rawValue }/appendix/${projectAppendixId.rawValue}/edit`,
                component: React.lazy( () => import('../components/projects/form/ProjectAppendixFormView') )
            }
        },
    },
    userGroups: {
        viewIdentifier: 'groups',
        routes: {
            main: {
                path: '/groups',
                exact: true,
                component: React.lazy( () => import('../components/user-groups/UserGroups') )
            },
            newEntry: {
                path: '/groups/new',
                exact: true,
                component: React.lazy( () => import('../components/user-groups/UserGroupsForm') )
            },
            detail: {
                path: '/groups/:userGroupId/detail',
                link: ( userGroupId: number ) => `/groups/${ userGroupId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/user-groups/UserGroupsDetailView'))
            },
            editEntry: {
                path: '/groups/:userGroupId/edit',
                link: ( userGroupId: number ) => `/groups/${ userGroupId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/user-groups/UserGroupsForm'))
            }
    }
    },
    employees: {
        viewIdentifier: 'employees',
        routes: {
            main: {
                path: '/employees',
                exact: true,
                component:  React.lazy( () => import('../components/employees/Employees') )
            },
            detail: {
                path: '/employees/:employeeId/detail',
                link: ( employeeId:number ) => `/employees/${ employeeId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/employees/EmployeesDetail'))
            },
            editEntry: {
                path: '/employees/:employeeId/edit',
                link: ( employeeId: number ) => `/employees/${ employeeId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/employees/EmployeesForm'))
            },
            newEntry: {
                path: '/employees/new',
                exact: true,
                component: React.lazy( () => import('../components/employees/EmployeesForm'))
            },
        }
    },
    employeeQualifications: {
        viewIdentifier: "employee_qualifications",
        routes: {}
    },
    qualifications: {
        viewIdentifier: "qualifications",
        routes: {
            main: {
                path: '/qualifications',
                exact: true,
                component:  React.lazy( () => import('../components/qualifications/QualificationListView') )
            },
            detail: {
                path: '/qualifications/:qualificationId/detail',
                link: ( qualificationId: number ) => `/qualifications/${ qualificationId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/qualifications/QualificationDetailView'))
            },
            editEntry: {
                path: '/qualifications/:qualificationId/edit',
                link: ( qualificationId: number ) => `/qualifications/${ qualificationId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/qualifications/QualificationFormView'))
            },
            newEntry: {
                path: '/qualifications/new',
                exact: true,
                component: React.lazy( () => import('../components/qualifications/QualificationFormView') )
            },
        }
    },
    employers: {
        viewIdentifier: EmployerModule.identifier,
        routes: {
            main: {
                path: '/employers',
                exact: true,
                component:  React.lazy( () => import('../components/employers/EmployerListView') )
            },
            detail: {
                path: '/employers/:employerId/detail',
                link: ( employerId: number ) => `/employers/${ employerId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/employers/EmployerDetailView'))
            },
            editEntry: {
                path: '/employers/:employerId/edit',
                link: ( employerId: number ) => `/employers/${ employerId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/employers/EmployerFormView'))
            },
            newEntry: {
                path: '/employers/new',
                exact: true,
                component: React.lazy( () => import('../components/employers/EmployerFormView') )
            },
        }
    },
    employersOffice: {
        viewIdentifier: 'employers_offices',
        routes: {

        }
    },
    customers: {
        viewIdentifier: CustomerModule.identifier,
        routes: {
            main: {
                path: '/customers',
                exact: true,
                component:  React.lazy( () => import('../components/customers/CustomerListView') )
            },
            detail: {
                path: '/customers/:customerId/detail',
                link: ( customerId: number ) => `/customers/${ customerId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/customers/CustomerDetailView'))
            },
            editEntry: {
                path: '/customers/:customerId/edit',
                link: ( customerId: number ) => `/customers/${ customerId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/customers/CustomerFormView'))
            },
            newEntry: {
                path: '/customers/new',
                exact: true,
                component: React.lazy( () => import('../components/customers/CustomerFormView') )
            },
        }
    },
    projectCommissionState: {
        viewIdentifier: ProjectCommissionStateModule.identifier,
        routes: {
            main: {
                path: '/project_commission_state',
                exact: true,
                component:  React.lazy( () => import('../components/project-commission-state/ProjectCommissionStateListView') )
            },
            detail: {
                path: '/project_commission_state/:commissionStateId/detail',
                link: ( commissionStateId: number ) => `/project_commission_state/${ commissionStateId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/project-commission-state/ProjectCommissionStateDetail'))
            },
            editEntry: {
                path: '/project_commission_state/:commissionStateId/edit',
                link: ( commissionStateId: number ) => `/project_commission_state/${ commissionStateId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/project-commission-state/ProjectCommissionStateFormView'))
            },
            newEntry: {
                path: '/project_commission_state/new',
                exact: true,
                component: React.lazy( () => import('../components/project-commission-state/ProjectCommissionStateFormView') )
            },
        }
    },
    projectRoles: {
        viewIdentifier: ProjectRoleModule.identifier,
        routes: {
            main: {
                path: '/project_roles',
                exact: true,
                component:  React.lazy( () => import('../components/project-roles/ProjectRolesListView') )
            },
            detail: {
                path: '/project_roles/:rolesId/detail',
                link: ( rolesId: number ) => `/project_roles/${ rolesId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/project-roles/ProjectRoleDetail'))
            },
            editEntry: {
                path: '/project_roles/:rolesId/edit',
                link: ( rolesId: number ) => `/project_roles/${ rolesId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/project-roles/ProjectRoleFormView'))
            },
            newEntry: {
                path: '/project_roles/new',
                exact: true,
                component: React.lazy( () => import('../components/project-roles/ProjectRoleFormView') )
            },
        }
    },
    contractors: {
        viewIdentifier: ContractorModule.identifier,
        routes: {
            main: {
                path: '/contractors',
                exact: true,
                component:  React.lazy( () => import('../components/contractors/ContractorListView') )
            },
            detail: {
                path: '/contractors/:contractorId/detail',
                link: ( contractorId: number ) => `/contractors/${ contractorId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/contractors/ContractorDetailView'))
            },
            editEntry: {
                path: '/contractors/:contractorId/edit',
                link: ( contractorId: number ) => `/contractors/${ contractorId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/contractors/ContractorFormView'))
            },
            newEntry: {
                path: '/contractors/new',
                exact: true,
                component: React.lazy( () => import('../components/contractors/ContractorFormView') )
            },
        },
    },
    externalWorkGroups: {
        viewIdentifier: ExternalWorkGroupModule.identifier,
        routes: {
            main: {
                path: '/external_work_groups',
                exact: true,
                component:  React.lazy( () => import('../components/contractors/ExternalWorkGroupListView') )
            },
            detail: {
                path: '/external_work_groups/:groupsId/detail',
                link: ( groupsId: number ) => `/external_work_groups/${ groupsId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/contractors/ExternalWorkGroupDetailView'))
            },
            editEntry: {
                path: '/external_work_groups/:groupsId/edit',
                link: ( groupsId: number ) => `/external_work_groups/${ groupsId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/contractors/ExternalWorkGroupFormView'))
            },
            newEntry: {
                path: '/external_work_groups/new',
                exact: true,
                component: React.lazy( () => import('../components/contractors/ExternalWorkGroupFormView') )
            },
        },
    },
    externalWorks: {
        viewIdentifier: ExternalWorkModule.identifier,
        routes: {
            main: {
                path: '/external_works',
                exact: true,
                component: React.lazy(() => import('../components/contractors/ExternalWorkListView'))
            },
            detail: {
                path: '/external_works/:workId/detail',
                link: (workId: number) => `/external_works/${workId}/detail`,
                exact: true,
                component: React.lazy(() => import('../components/contractors/ExternalWorkDetailView'))
            },
            editEntry: {
                path: '/external_works/:workId/edit',
                link: (workId: number) => `/external_works/${workId}/edit`,
                exact: true,
                component: React.lazy(() => import('../components/contractors/ExternalWorkFormView'))
            },
            newEntry: {
                path: '/external_works/new',
                exact: true,
                component: React.lazy(() => import('../components/contractors/ExternalWorkFormView'))
            },
        }
    },
    calendar: {
        viewIdentifier: TimesheetModule.identifier,
        routes: {
            main: {
                path: '/calendar/:calView?/:startAt?',
                link: ( calView: string, startAt: number ) => `/calendar/${ calView }/${ startAt }`,
                hash: '#grid-times-07:00:00',
                component: React.lazy(() => import('../components/calendar/CalendarMain'))
            }
        }
    },
    user: {
        viewIdentifier: 'user_cp',
        routes: {
        }
    },
    accountBook: {
        viewIdentifier: OutgoingInvoiceModule.identifier,
        routes: {
            detail: {
                path: '/account_book/:outgoingInvoiceId/detail',
                link: ( outgoingInvoiceId: number ) => `/account_book/${ outgoingInvoiceId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/account-book/detail/OutgoingInvoiceDetailView') )
            },
            main: {
                path: '/account_book',
                exact: true,
                component: React.lazy(() => import('../components/account-book/OutgoingInvoiceView'))
            },
            newEntry: {
                path: '/account_book/new',
                exact: true,
                component: React.lazy( () => import('../components/account-book/form/OutgoingInvoiceFormView') )
            },
            editEntry: {
                path: '/account_book/:outgoingInvoiceId/edit',
                link: ( outgoingInvoiceId: number ) => `/account_book/${ outgoingInvoiceId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/account-book/form/OutgoingInvoiceFormView') )
            }
        }
    },
    incomingInvoice: {
        viewIdentifier: IncomingInvoiceModule.identifier,
        routes: {
            detail: {
                path: '/incoming_invoice/:incomingInvoiceId/detail',
                link: ( incomingInvoiceId: number ) => `/incoming_invoice/${ incomingInvoiceId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/incoming-invoice/detail/IncomingInvoiceDetailView') )
            },
            main: {
                path: '/incoming_invoice',
                exact: true,
                component: React.lazy(() => import('../components/incoming-invoice/IncomingInvoiceView'))
            },
            newEntry: {
                path: '/incoming_invoice/new',
                exact: true,
                component: React.lazy( () => import('../components/incoming-invoice/form/IncomingInvoiceFormView') )
            },
            editEntry: {
                path: '/incoming_invoice/:incomingInvoiceId/edit',
                link: ( incomingInvoiceId: number ) => `/incoming_invoice/${ incomingInvoiceId }/edit`,
                exact: true,
                component: React.lazy( () => import('../components/incoming-invoice/form/IncomingInvoiceFormView') )
            }
        }
    },
    employeeHoursReport: {
        viewIdentifier: TimesheetModule.identifier,
        routes: {
            main: {
                path: '/reporting_employees_hours',
                exact: true,
                component: React.lazy(() => import('../components/reporting/EmployeeHourReportView') )
            }
        }
    },
    projectReport: {
        viewIdentifier: ProjectReportModule.identifier,
        routes: {
            main: {
                path: '/project_report',
                exact: true,
                component: React.lazy(() => import('../components/reporting/ProjectReportView') )
            }
        }
    },
    projectSolvencyReport: {
        viewIdentifier: ProjectSolvencyReportModule.identifier,
        routes: {
            main: {
                path: '/solvency_projection',
                exact: true,
                component: React.lazy(() => import('../components/reporting/ProjectSolvencyReportView') )
            },
            detail: {
                path: '/solvency_projection/:projectId',
                link: ( projectId: Identifier<number> ) => `/solvency_projection/${ projectId.rawValue }`,
                exact: true,
                component: React.lazy( () => import('../components/reporting/ProjectSolvencyReportDetailView') )
            }
        }
    },
    adminView: {
        viewIdentifier: "admin",
        routes: {
            main: {
                path: '/admin',
                exact: true,
                component: React.lazy(() => import('../components/admin/AdminListView'))
            }
        }
    },
    about: {
        viewIdentifier: "about",
        routes: {
            main: {
                path: '/about',
                authenticationOptional: true,
                exact: true,
                component: React.lazy(() => import('../components/about/About'))
            }
        }
    },
    login: {
        viewIdentifier: "login",
        routes: {
            main: {
                path: '/login',
                exact: true,
                authenticationOptional: true,
                component: React.lazy(() => import('../components/app-bar/LoginForm'))
            }
        }
    },
    register: {
        viewIdentifier: "register",
        routes: {
            main: {
                path: '/register',
                exact: true,
                authenticationOptional: true,
                component: React.lazy(() => import('../components/register/RegisterForm'))
            }
        }
    },
    aiChat: {
        viewIdentifier: "aiChat",
        routes: {
            main: {
                path: "/ai-chat",
                exact: true,
                authenticationOptional: true,
                component: React.lazy(() => import('../components/ai-chat/AiChat'))
            }
        }
    },
    api_keys: {
        viewIdentifier: UserApiKeyModule.identifier,
        routes: {
            main: {
                path: "/api_keys",
                exact: true,
                authenticationOptional: true,
                component: React.lazy(() => import('../components/api-keys/ApiKeysView'))
            },
            detail: {
                path: '/api_keys/:apiKeyId/detail',
                link: ( apiKeyId: string ) => `/api_keys/${ apiKeyId }/detail`,
                exact: true,
                component: React.lazy( () => import('../components/api-keys/detail/ApiKeysDetailView') )
            },
            newEntry: {
                path: '/api_keys/new',
                exact: true,
                component: React.lazy( () => import('../components/api-keys/form/ApiKeysFormView') )
            },
        }
    }

};

interface WindowLocationRedirectWrapperProps {
    path: string
}
const WindowLocationRedirectWrapper = ( { path }: WindowLocationRedirectWrapperProps ) => {
    window.location.href = `${ path }`;
    return null;
};